import { StandardEvents } from '@shopify/events';

class ProductTabs extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', (event) => {
      const trigger = event.target.closest('.product-tabs__trigger');
      if (!trigger) return;

      this.#activateTab(trigger.dataset.tab);
    });

    this.#organizeNav();

    // This section is a sibling of the product-information section (not nested inside it),
    // so the productSelect event never bubbles through our own .shopify-section wrapper —
    // listen on the document instead.
    document.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    document.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  // Each tab block renders its own trigger button next to its panel. Move the
  // triggers into the shared nav bar so they render as a row above the panels,
  // then activate whichever tab was previously active (or the first one).
  #organizeNav(preferredTab) {
    const nav = this.querySelector('.product-tabs__nav');
    if (!nav) return;

    this.querySelectorAll('.product-tabs__trigger').forEach((trigger) => nav.appendChild(trigger));

    // A tab's block (e.g. spec table) can render nothing for a given product/variant.
    // Hide those tabs rather than showing an empty panel.
    this.querySelectorAll('.product-tabs__panel').forEach((panel) => {
      const trigger = nav.querySelector(`.product-tabs__trigger[data-tab="${panel.dataset.panel}"]`);
      if (trigger) trigger.hidden = panel.textContent.trim() === '';
    });

    const visibleTriggers = [...nav.querySelectorAll('.product-tabs__trigger')].filter((trigger) => !trigger.hidden);
    const fallbackTab = visibleTriggers[0]?.dataset.tab;
    const target = preferredTab && visibleTriggers.some((trigger) => trigger.dataset.tab === preferredTab)
      ? preferredTab
      : fallbackTab;

    this.#activateTab(target);
  }

  #activateTab(target) {
    if (!target) return;

    this.querySelectorAll('.product-tabs__trigger').forEach((button) => {
      const isActive = button.dataset.tab === target;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-selected', String(isActive));
    });

    this.querySelectorAll('.product-tabs__panel').forEach((panel) => {
      panel.style.display = panel.dataset.panel === target ? 'block' : 'none';
    });
  }

  #handleProductSelect = (event) => {
    event.promise
      .then(async () => {
        // Section Rendering API instead of the swap event's own payload: this section is a
        // sibling of product-information (not nested inside it), and the payload Shopify
        // hands back for out-of-group sections during a combined-listing swap comes back with
        // the tab blocks stripped out. Fetching our own section fragment sidesteps that.
        const sectionId = this.dataset.sectionId;
        const response = await fetch(`${location.pathname}?section_id=${sectionId}`);
        if (!response.ok) return;

        const html = await response.text();
        const newProductTabs = new DOMParser()
          .parseFromString(html, 'text/html')
          .querySelector(`product-tabs[data-section-id="${sectionId}"]`);
        if (!newProductTabs) return;

        const activeTrigger = this.querySelector('.product-tabs__trigger.is-active');
        const activeTab = activeTrigger?.dataset.tab;

        this.innerHTML = newProductTabs.innerHTML;
        this.#organizeNav(activeTab);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-tabs] Event promise rejected:', error);
      });
  };
}

customElements.define('product-tabs', ProductTabs);
